import { useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCartStore } from "@/store/CartStore";
import {
  allMenusQueryOptions,
  type RestaurantMenu,
  type MenuCategory,
  type MenuItem,
} from "./useMenu";

interface MenuUpdateOptions {
  /** Interval in milliseconds for checking menu updates (default: 30 seconds) */
  updateInterval?: number;
  /** Whether to enable the watcher (default: true) */
  enabled?: boolean;
  /** Callback fired when menu is updated */
  onMenuUpdated?: (updatedMenu: RestaurantMenu) => void;
  /** Callback fired when items are removed from cart */
  onUnavailableItemsRemoved?: (removedItems: string[]) => void;
}

interface CartItemAvailability {
  lineId: string;
  isAvailable: boolean;
  productName: string;
}

export function useMenuUpdateWatcher(
  locationId: string,
  currentMenuId?: string,
  options: MenuUpdateOptions = {}
) {
  const {
    updateInterval = 30000, // 30 seconds
    enabled = true,
    onMenuUpdated,
    onUnavailableItemsRemoved,
  } = options;

  const queryClient = useQueryClient();
  const { cart, remove: removeFromCart, addRemovedItem } = useCartStore();
  const intervalRef = useRef<NodeJS.Timeout>();
  const lastMenuHashRef = useRef<string>();

  // Store current values in refs to avoid stale closures
  const currentLocationIdRef = useRef(locationId);
  const currentMenuIdRef = useRef(currentMenuId);
  const onMenuUpdatedRef = useRef(onMenuUpdated);
  const onUnavailableItemsRemovedRef = useRef(onUnavailableItemsRemoved);

  // Update refs when values change
  useEffect(() => {
    currentLocationIdRef.current = locationId;
    currentMenuIdRef.current = currentMenuId;
    onMenuUpdatedRef.current = onMenuUpdated;
    onUnavailableItemsRemovedRef.current = onUnavailableItemsRemoved;
  }, [locationId, currentMenuId, onMenuUpdated, onUnavailableItemsRemoved]);

  // Generate a simple hash of the menu structure for change detection
  const generateMenuHash = useCallback((menu: RestaurantMenu): string => {
    const hashData = {
      categories: menu.categories?.map((cat: MenuCategory) => ({
        id: cat.id,
        name: cat.name,
        products: cat.products?.map((prod: MenuItem) => ({
          id: prod.id,
          name: prod.name,
          unavailable: prod.unavailable,
        })),
      })),
    };
    return JSON.stringify(hashData);
  }, []);

  // Check cart item availability against current menu
  const checkCartItemAvailability = useCallback(
    (menu: RestaurantMenu): CartItemAvailability[] => {
      const availability: CartItemAvailability[] = [];


      for (const cartItem of cart) {
        let isAvailable = false;
        let productName = cartItem.name || "Unknown Product";

        // Find the product in the menu
        for (const category of menu.categories || []) {
          const product = category.products?.find((p: MenuItem) => {
            return p.id === cartItem.productId;
          });

          if (product) {
            productName = product.name;
            // Product is available if unavailable is false, null, or undefined
            if (product.unavailable !== true) {
              isAvailable = true;
              break;
            }
          }
        }


        availability.push({
          lineId: cartItem.lineId,
          isAvailable,
          productName,
        });
      }

      return availability;
    },
    [cart]
  );

  // Remove unavailable items from cart
  const removeUnavailableItems = useCallback(
    (availability: CartItemAvailability[]) => {
      const removedItems: string[] = [];
      const unavailableItems = availability.filter((item) => !item.isAvailable);

      unavailableItems.forEach(({ lineId, productName }) => {
        removeFromCart(lineId);
        addRemovedItem(productName);
        removedItems.push(productName);
      });

      if (removedItems.length > 0) {
        onUnavailableItemsRemovedRef.current?.(removedItems);
      }
    },
    [removeFromCart, addRemovedItem]
  );

  // Create a stable callback for menu updates
  const performMenuUpdate = useCallback(async () => {
    const currentLocationId = currentLocationIdRef.current;
    const currentMenuId = currentMenuIdRef.current;

    if (!currentLocationId) return;

    try {
      // Always invalidate and fetch fresh data to check for updates
      await queryClient.invalidateQueries({
        queryKey: allMenusQueryOptions(currentLocationId).queryKey,
      });
      // Fetch the fresh data
      const menuData = await queryClient.fetchQuery(
        allMenusQueryOptions(currentLocationId)
      );

      if (!menuData || !Array.isArray(menuData)) {
        return;
      }

      // Find current menu if specified
      const currentMenu = currentMenuId
        ? menuData.find((menu: RestaurantMenu) => menu.id === currentMenuId)
        : menuData[0]; // Fallback to first menu

      if (!currentMenu) {
        return;
      }

      // Generate hash of current menu
      const currentMenuHash = generateMenuHash(currentMenu);

      // Initialize hash on first run
      if (!lastMenuHashRef.current) {
        lastMenuHashRef.current = currentMenuHash;
        return;
      }

      // Check if menu has changed
      if (lastMenuHashRef.current !== currentMenuHash) {

        // Check cart item availability
        const availability = checkCartItemAvailability(currentMenu);
        const unavailableItems = availability.filter(
          (item) => !item.isAvailable
        );

        // Remove unavailable items
        if (unavailableItems.length > 0) {
          removeUnavailableItems(availability);
        }

        // Notify about menu update using ref
        onMenuUpdatedRef.current?.(currentMenu);

        // Update the last known menu hash
        lastMenuHashRef.current = currentMenuHash;
      }
    } catch (error) {
      console.error("Error checking for menu updates:", error);
    }
  }, [
    queryClient,
    generateMenuHash,
    checkCartItemAvailability,
    removeUnavailableItems,
  ]);

  // Expose the function for external use
  const checkForMenuUpdates = performMenuUpdate;

  // Set up periodic checking
  useEffect(() => {
    if (!enabled || !locationId) return;


    // Initial check after a short delay
    const initialTimeout = setTimeout(() => {
      performMenuUpdate();
    }, 1000);

    // Set up interval for periodic checks
    intervalRef.current = setInterval(() => {
      performMenuUpdate();
    }, updateInterval);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, locationId, updateInterval, performMenuUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    checkForMenuUpdates,
  };
}
